/**
 * Check-ins Page - Constants and Utilities
 */

import { Timestamp } from 'firebase/firestore';
import type { CheckIn, KeyResult } from '../../models';

/**
 * Mock key results for filtering
 */
export const MOCK_KEY_RESULTS: KeyResult[] = [
  {
    id: 'kr-1',
    objectiveId: 'obj-1',
    objectiveTitle: 'Aumentar receita em 30%',
    cycleId: 'cycle-1',
    title: 'Aumentar receita recorrente para R$ 500k/mês',
    description: 'Crescimento sustentável da receita mensal',
    metricType: 'currency',
    unit: 'k',
    initialValue: 300,
    currentValue: 420,
    targetValue: 500,
    progress: 60,
    status: 'on_track',
    responsibleId: 'user-1',
    responsibleName: 'João Silva',
    responsibleEmail: 'joao@example.com',
    lastCheckInDate: Timestamp.fromDate(new Date('2025-01-26')),
    checkInCount: 3,
    startDate: Timestamp.fromDate(new Date('2025-01-01')),
    targetDate: Timestamp.fromDate(new Date('2025-03-31')),
    completedAt: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-15')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-26')),
    _metadata: { version: 1, lastModifiedBy: 'user-1' },
  },
  {
    id: 'kr-2',
    objectiveId: 'obj-2',
    objectiveTitle: 'Melhorar satisfação do cliente para 90%',
    cycleId: 'cycle-1',
    title: 'Atingir NPS de 90',
    description: 'Melhorar experiência e satisfação dos clientes',
    metricType: 'number',
    unit: '',
    initialValue: 65,
    currentValue: 78,
    targetValue: 90,
    progress: 52,
    status: 'at_risk',
    responsibleId: 'user-2',
    responsibleName: 'Maria Santos',
    responsibleEmail: 'maria@example.com',
    lastCheckInDate: Timestamp.fromDate(new Date('2025-01-25')),
    checkInCount: 2,
    startDate: Timestamp.fromDate(new Date('2025-01-01')),
    targetDate: Timestamp.fromDate(new Date('2025-03-31')),
    completedAt: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-10')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-25')),
    _metadata: { version: 1, lastModifiedBy: 'user-2' },
  },
];

/**
 * Mock check-ins data (5 check-ins)
 */
export const MOCK_CHECKINS: CheckIn[] = [
  {
    id: 'checkin-1',
    checkInDate: Timestamp.fromDate(new Date('2025-01-26')),
    previousValue: 400,
    newValue: 420,
    progressChange: 10,
    impediments: 'Atraso na liberação de budget para marketing',
    notes: 'Crescimento acima do esperado apesar dos desafios. Campanha de marketing teve ótimos resultados.',
    actionsRequired: 'Acelerar aprovação de budget adicional para Q2',
    recordedById: 'user-1',
    recordedByName: 'João Silva',
    cycleEventId: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-26T14:30:00')),
  },
  {
    id: 'checkin-2',
    checkInDate: Timestamp.fromDate(new Date('2025-01-19')),
    previousValue: 380,
    newValue: 400,
    progressChange: 10,
    notes: 'Semana positiva com novos contratos fechados. Time de vendas performou bem.',
    recordedById: 'user-1',
    recordedByName: 'João Silva',
    cycleEventId: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-19T10:15:00')),
  },
  {
    id: 'checkin-3',
    checkInDate: Timestamp.fromDate(new Date('2025-01-25')),
    previousValue: 72,
    newValue: 78,
    progressChange: 6,
    impediments: 'Reclamações sobre tempo de resposta do suporte aumentaram',
    notes: 'Melhoria gradual mas ainda abaixo da meta. Precisamos focar em treinamento do time de CS.',
    actionsRequired: 'Implementar programa de capacitação para CS e reduzir SLA de resposta',
    recordedById: 'user-2',
    recordedByName: 'Maria Santos',
    cycleEventId: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-25T16:45:00')),
  },
  {
    id: 'checkin-4',
    checkInDate: Timestamp.fromDate(new Date('2025-01-12')),
    previousValue: 360,
    newValue: 380,
    progressChange: 10,
    notes: 'Início promissor do trimestre. Dois grandes clientes renovaram contratos.',
    recordedById: 'user-1',
    recordedByName: 'João Silva',
    cycleEventId: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-12T11:20:00')),
  },
  {
    id: 'checkin-5',
    checkInDate: Timestamp.fromDate(new Date('2025-01-18')),
    previousValue: 68,
    newValue: 72,
    progressChange: 4,
    notes: 'Pequena melhoria após implementação de novo fluxo de onboarding.',
    recordedById: 'user-2',
    recordedByName: 'Maria Santos',
    cycleEventId: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-18T09:30:00')),
  },
];

/**
 * Format date and time
 */
export function formatCheckInDate(date: Date): string {
  const dateStr = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timeStr = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateStr} às ${timeStr}`;
}

/**
 * Get confidence change indicator
 */
export function getConfidenceChangeIndicator(change: number): {
  symbol: string;
  color: string;
} {
  if (change > 0) {
    return { symbol: '↑', color: 'text-green-600' };
  } else if (change < 0) {
    return { symbol: '↓', color: 'text-red-600' };
  }
  return { symbol: '→', color: 'text-gray-600' };
}
